<template>
    <ul
        data-testid="mention-dropdown"
        class="z-50 max-h-48 w-64 max-w-full list-none overflow-y-auto rounded-sm border border-gray-200 bg-white py-1 shadow-[0px_4px_16px_0px_rgba(0,0,0,0.12)]"
        :class="{ absolute: positioned }"
    >
        <li
            data-testid="mention-option"
            class="cursor-pointer px-3 py-1.5 text-sm text-gray-700"
            :class="index === activeIndex ? 'bg-gray-100' : 'hover:bg-gray-50'"
            :key="item.id"
            @mousedown.prevent="emit('select', item)"
            @mousemove="emit('hover', index)"
            v-for="(item, index) in items"
        >
            {{ item.fullName }}
        </li>
    </ul>
</template>

<script setup lang="ts">
    /**
     * Floating list of mentionable users for the inline @mention composer.
     *
     * Purely presentational: the parent owns filtering, active-index state, and
     * what selection does. Selection fires on `mousedown` (prevented) rather than
     * `click` so the host textarea keeps focus and its caret through the pick.
     * The active option is shown by background, driven by `activeIndex`.
     */

    /** Minimal shape an option needs to render and report a selection. */
    type MentionOption = {
        readonly id: number | string;
        readonly fullName: string;
    };

    withDefaults(
        defineProps<{
            /** Mentionable users to list, in display order. */
            items: ReadonlyArray<MentionOption>;
            /** Index of the keyboard-highlighted option. */
            activeIndex?: number;
            /** Whether the dropdown positions itself or is hosted by a popover. */
            positioned?: boolean;
        }>(),
        { activeIndex: 0, positioned: true }
    );

    const emit = defineEmits<{
        select: [item: MentionOption];
        hover: [index: number];
    }>();
</script>
